/**
 * ============================================================================
 * [P6-01] Firestore Posts Service
 *
 * 트래킹 포스트(posts) 컬렉션에 대한 CRUD 및 실시간 구독 함수
 *
 * 기능:
 * - createPost: 포스트 생성
 * - updatePost: 포스트 수정
 * - deletePost: 포스트 삭제
 * - subscribeToPosts: 실시간 구독
 *
 * Safety:
 * - withRetry()로 네트워크 에러 대응
 * - 모든 함수에서 userId/postId 검증
 * - onSnapshot unsubscribe 반환으로 메모리 누수 방지
 * ============================================================================
 */

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  query,
  orderBy,
  onSnapshot,
  serverTimestamp,
  type DocumentSnapshot,
  type CollectionReference,
  type Timestamp,
} from 'firebase/firestore'
import { db } from '../firebase'
import { withRetry } from '@/utils/common'
import type { TrackingPost, TrackingMetric } from '@/types'

// ============================================================================
// Firestore 문서 데이터 타입 정의
// ============================================================================
export interface FirestorePostData {
  title: string
  content?: string
  platform: string
  url?: string
  userId: string
  isFavorite?: boolean
  isActive?: boolean
  metrics: TrackingMetric[]
  createdAt: Timestamp | ReturnType<typeof serverTimestamp>
  updatedAt: ReturnType<typeof serverTimestamp>
}

// ============================================================================
// 컬렉션 참조 헬퍼
// ============================================================================
const getPostsCollection = (userId: string): CollectionReference =>
  collection(db, 'users', userId, 'posts')

// ============================================================================
// [P6-01] 변환 함수: TrackingPost → Firestore 문서 데이터
// ============================================================================
export function postToFirestore(
  post: Omit<TrackingPost, 'id' | 'createdAt' | 'updatedAt'>
): Omit<FirestorePostData, 'createdAt' | 'updatedAt'> & {
  createdAt: ReturnType<typeof serverTimestamp>
  updatedAt: ReturnType<typeof serverTimestamp>
} {
  return {
    title: post.title ?? '',
    content: post.content ?? '',
    platform: post.platform ?? 'threads',
    url: post.url ?? '',
    userId: post.userId ?? '',
    isFavorite: post.isFavorite ?? false,
    isActive: post.isActive ?? true,
    metrics: post.metrics ?? [],
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  }
}

// ============================================================================
// [P6-01] 변환 함수: Firestore 스냅샷 → TrackingPost
// ============================================================================
export function postFromFirestore(snapshot: DocumentSnapshot): TrackingPost {
  const data = snapshot.data()

  if (!data) {
    throw new Error(`[postFromFirestore] Document data is null for id: ${snapshot.id}`)
  }

  // Timestamp → Date 변환 헬퍼
  const toDate = (timestamp: Timestamp | null | undefined): Date => {
    if (!timestamp) return new Date()
    if (typeof timestamp.toDate === 'function') {
      return timestamp.toDate()
    }
    return new Date()
  }

  return {
    id: snapshot.id,
    title: data.title ?? '',
    content: data.content ?? '',
    platform: data.platform ?? 'threads',
    url: data.url ?? '',
    userId: data.userId ?? '',
    isFavorite: data.isFavorite ?? false,
    isActive: data.isActive ?? true,
    metrics: Array.isArray(data.metrics) ? data.metrics : [],
    createdAt: toDate(data.createdAt),
    updatedAt: toDate(data.updatedAt),
  }
}

// ============================================================================
// [P6-01] 부분 업데이트용 변환 함수
// ============================================================================
export function postUpdatesToFirestore(
  updates: Partial<TrackingPost>
): Partial<Omit<FirestorePostData, 'createdAt'>> {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { id: _id, createdAt: _createdAt, updatedAt: _updatedAt, ...safeUpdates } = updates

  const result: Partial<Omit<FirestorePostData, 'createdAt'>> = {
    updatedAt: serverTimestamp(),
  }

  // undefined 값 제거 - Firestore는 undefined를 허용하지 않음
  Object.entries(safeUpdates).forEach(([key, value]) => {
    if (value !== undefined) {
      (result as Record<string, unknown>)[key] = value
    }
  })

  return result
}

// ============================================================================
// [P6-01-C] 포스트 생성 (Create)
// ============================================================================
export async function createPost(
  userId: string,
  postData: Omit<TrackingPost, 'id' | 'createdAt' | 'updatedAt' | 'userId'>
): Promise<TrackingPost> {
  if (!userId) {
    throw new Error('[createPost] userId is required')
  }
  if (!postData.title?.trim()) {
    throw new Error('[createPost] title is required')
  }

  return withRetry(async () => {
    const postsRef = getPostsCollection(userId)
    const docData = postToFirestore({ ...postData, userId })

    const docRef = await addDoc(postsRef, docData)
    const newDoc = await getDoc(docRef)

    if (!newDoc.exists()) {
      throw new Error('[createPost] Failed to create document')
    }

    console.log('[createPost] 포스트 생성 완료:', docRef.id)
    return postFromFirestore(newDoc)
  }, 3, 1000)
}

// ============================================================================
// [P6-01-D] 포스트 수정 (Update)
// ============================================================================
export async function updatePost(
  userId: string,
  postId: string,
  updates: Partial<TrackingPost>
): Promise<void> {
  if (!userId) {
    throw new Error('[updatePost] userId is required')
  }
  if (!postId) {
    throw new Error('[updatePost] postId is required')
  }

  const updateKeys = Object.keys(updates).filter(
    (key) => key !== 'id' && key !== 'createdAt'
  )
  if (updateKeys.length === 0) {
    console.warn('[updatePost] No valid updates provided, skipping')
    return
  }

  return withRetry(async () => {
    const postRef = doc(db, 'users', userId, 'posts', postId)
    const safeUpdates = postUpdatesToFirestore(updates)

    await updateDoc(postRef, safeUpdates)
    console.log('[updatePost] 포스트 수정 완료:', postId)
  }, 3, 1000)
}

// ============================================================================
// [P6-01-E] 포스트 삭제 (Delete)
// ============================================================================
export async function deletePost(
  userId: string,
  postId: string
): Promise<void> {
  if (!userId) {
    throw new Error('[deletePost] userId is required')
  }
  if (!postId) {
    throw new Error('[deletePost] postId is required')
  }

  return withRetry(async () => {
    const postRef = doc(db, 'users', userId, 'posts', postId)
    await deleteDoc(postRef)
    console.log('[deletePost] 포스트 삭제 완료:', postId)
  }, 3, 1000)
}

// ============================================================================
// [P6-01-F] 실시간 구독 (Subscribe)
// - CRITICAL: 반환된 unsubscribe 함수를 반드시 cleanup에서 호출할 것
// ============================================================================
export function subscribeToPosts(
  userId: string,
  onData: (posts: TrackingPost[]) => void,
  onError?: (error: Error) => void
): () => void {
  if (!userId) {
    console.warn('[subscribeToPosts] userId is null, returning no-op unsubscribe')
    return () => {
      // no-op
    }
  }

  const postsRef = getPostsCollection(userId)
  const q = query(postsRef, orderBy('updatedAt', 'desc'))

  console.log('[subscribeToPosts] 실시간 구독 시작:', userId)

  const unsubscribe = onSnapshot(
    q,
    (snapshot) => {
      const posts = snapshot.docs.map((docSnap) => postFromFirestore(docSnap))
      console.log('[subscribeToPosts] 데이터 수신:', posts.length, '개')
      onData(posts)
    },
    (error) => {
      console.error('[subscribeToPosts] 구독 에러:', error)
      onError?.(error)
    }
  )

  return () => {
    console.log('[subscribeToPosts] 구독 해제:', userId)
    unsubscribe()
  }
}

// ============================================================================
// [P6-01-G] 메트릭 추가 (Add Metric to Post)
// ============================================================================
export async function addMetricToPost(
  userId: string,
  postId: string,
  metric: Omit<TrackingMetric, 'id'>
): Promise<void> {
  if (!userId || !postId) {
    throw new Error('[addMetricToPost] userId and postId are required')
  }

  return withRetry(async () => {
    const postRef = doc(db, 'users', userId, 'posts', postId)
    const postDoc = await getDoc(postRef)

    if (!postDoc.exists()) {
      throw new Error('[addMetricToPost] Post not found')
    }

    const post = postFromFirestore(postDoc)
    const newMetric: TrackingMetric = {
      ...metric,
      id: `metric_${Date.now()}`,
    }

    await updateDoc(postRef, {
      metrics: [...post.metrics, newMetric],
      updatedAt: serverTimestamp(),
    })

    console.log('[addMetricToPost] 메트릭 추가 완료:', postId)
  }, 3, 1000)
}

// ============================================================================
// [P6-01-H] 메트릭 수정 (Update Metric in Post)
// ============================================================================
export async function updateMetricInPost(
  userId: string,
  postId: string,
  metricId: string,
  updates: Partial<TrackingMetric>
): Promise<void> {
  if (!userId || !postId || !metricId) {
    throw new Error('[updateMetricInPost] userId, postId, and metricId are required')
  }

  return withRetry(async () => {
    const postRef = doc(db, 'users', userId, 'posts', postId)
    const postDoc = await getDoc(postRef)

    if (!postDoc.exists()) {
      throw new Error('[updateMetricInPost] Post not found')
    }

    const post = postFromFirestore(postDoc)
    const updatedMetrics = post.metrics.map((m) =>
      m.id === metricId ? { ...m, ...updates } : m
    )

    await updateDoc(postRef, {
      metrics: updatedMetrics,
      updatedAt: serverTimestamp(),
    })

    console.log('[updateMetricInPost] 메트릭 수정 완료:', metricId)
  }, 3, 1000)
}

// ============================================================================
// [P6-01-I] 메트릭 삭제 (Delete Metric from Post)
// ============================================================================
export async function deleteMetricFromPost(
  userId: string,
  postId: string,
  metricId: string
): Promise<void> {
  if (!userId || !postId || !metricId) {
    throw new Error('[deleteMetricFromPost] userId, postId, and metricId are required')
  }

  return withRetry(async () => {
    const postRef = doc(db, 'users', userId, 'posts', postId)
    const postDoc = await getDoc(postRef)

    if (!postDoc.exists()) {
      throw new Error('[deleteMetricFromPost] Post not found')
    }

    const post = postFromFirestore(postDoc)
    const updatedMetrics = post.metrics.filter((m) => m.id !== metricId)

    await updateDoc(postRef, {
      metrics: updatedMetrics,
      updatedAt: serverTimestamp(),
    })

    console.log('[deleteMetricFromPost] 메트릭 삭제 완료:', metricId)
  }, 3, 1000)
}

// ============================================================================
// [P2-01] 다중 메트릭 일괄 삭제 (Batch Delete Metrics from Post)
// - 여러 메트릭을 한 번의 Firestore 호출로 삭제
// - Set을 사용하여 O(n) 시간복잡도로 필터링
// ============================================================================
export async function batchDeleteMetricsFromPost(
  userId: string,
  postId: string,
  metricIds: string[]
): Promise<void> {
  // 1. Validation
  if (!userId) {
    throw new Error('[batchDeleteMetricsFromPost] userId required')
  }
  if (!postId) {
    throw new Error('[batchDeleteMetricsFromPost] postId required')
  }
  if (!metricIds || metricIds.length === 0) {
    console.warn('[batchDeleteMetricsFromPost] No metrics to delete')
    return
  }

  // 2. withRetry로 네트워크 에러 대응
  return withRetry(async () => {
    // 2-1. postRef 생성
    const postRef = doc(db, 'users', userId, 'posts', postId)

    // 2-2. 현재 포스트 조회
    const postDoc = await getDoc(postRef)
    if (!postDoc.exists()) {
      throw new Error('[batchDeleteMetricsFromPost] Post not found')
    }

    // 2-3. 포스트 데이터 변환
    const post = postFromFirestore(postDoc)

    // 2-4. 삭제할 메트릭 ID를 Set으로 변환 (O(1) 조회)
    const metricIdSet = new Set(metricIds)

    // 2-5. 삭제 대상 제외한 메트릭만 필터링
    const remainingMetrics = post.metrics.filter(
      (m) => !metricIdSet.has(m.id)
    )

    // 2-6. 단일 updateDoc 호출로 업데이트
    await updateDoc(postRef, {
      metrics: remainingMetrics,
      updatedAt: serverTimestamp(),
    })

    console.log(`[batchDeleteMetricsFromPost] ${metricIds.length}개 삭제 완료`)
  }, 3, 1000) // 3회 재시도, 1초 간격
}
